import { gql } from '@apollo/client';

import type { ApplePayDetails, GooglePayDetails } from '@fhs/cart/src/payment/payment-types';
import type { CardData } from '@fhs/cart/src/payment/payment-types';
import { EncryptionService } from '@fhs/cart/src/payment/services/encryption-service';
import { queryClient, useMutation } from '@fhs/client';
import { UserAccountsDocument } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { PaymentProcessor } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { client } from '@fhs-legacy/frontend/src/state/graphql/client';
import { useLocale } from '@fhs-legacy/frontend/src/state/intl';
import { getCountryAndCurrencyCodes } from '@fhs-legacy/frontend/src/utils/form/constants';

/**
 * GraphQL enum for payment method types (sent to the backend)
 */
enum PaymentMethodType {
  SAVED_CARD = 'SAVED_CARD',
  NEW_CARD = 'NEW_CARD',
  APPLE_PAY = 'APPLE_PAY',
  GOOGLE_PAY = 'GOOGLE_PAY',
}

/**
 * Frontend enum for payment method types (used in the UI)
 */
export enum FrontendPaymentMethodType {
  SAVED_CARD = 'savedCard',
  NEW_CARD = 'newCard',
  APPLE_PAY = 'applePay',
  GOOGLE_PAY = 'googlePay',
}

type PaymentMethodInput = {
  id: string;
  type: PaymentMethodType;
};

type GooglePayInput = {
  token: string;
  billingAddress?: {
    postalCode?: string;
    locality?: string;
    region?: string;
    streetAddress?: string;
  };
};

type ApplePayInput = {
  token: string;
  signature: string;
  data: string;
  version: string;
  ephemeralPublicKey: string;
  publicKeyHash: string;
  transactionId: string;
  billingAddress?: {
    postalCode?: string;
    locality?: string;
    region?: string;
    streetAddress?: string;
  };
};

type WorldpayInput = {
  lowValueToken?: string;
};

type BillingAddressInput = {
  postalCode?: string;
  locality?: string;
  region?: string;
  streetAddress?: string;
};

type ReloadGiftCardInput = {
  amount: number;
  paymentMethod: PaymentMethodInput;
  currency: string;
  platform?: 'app' | 'web' | 'kiosk';
  storePaymentMethod?: boolean;
  googlePay?: GooglePayInput;
  applePay?: ApplePayInput;
  worldpayInput?: WorldpayInput;
  billingAddress?: BillingAddressInput;
};

/**
 * Maps frontend payment method type to GraphQL enum
 */
function mapPaymentMethodType(type: FrontendPaymentMethodType): PaymentMethodType {
  switch (type) {
    case FrontendPaymentMethodType.SAVED_CARD:
      return PaymentMethodType.SAVED_CARD;
    case FrontendPaymentMethodType.NEW_CARD:
      return PaymentMethodType.NEW_CARD;
    case FrontendPaymentMethodType.APPLE_PAY:
      return PaymentMethodType.APPLE_PAY;
    case FrontendPaymentMethodType.GOOGLE_PAY:
      return PaymentMethodType.GOOGLE_PAY;
    default:
      throw new Error(`Unsupported payment method type: ${type}`);
  }
}

const FhsReloadGiftCardInput = gql`
  mutation ReloadGiftCard($input: FhsReloadGiftCardInput!) @gateway @authRequired {
    fhsReloadGiftCard(input: $input) {
      giftCardDetails {
        cardType
        currentBalance
        last4
      }
    }
  }
`;

type ReloadGiftCardMutationInput = {
  amount: number;
  paymentMethodId: string;
  paymentMethodType: FrontendPaymentMethodType;
  platform?: string;
  storePaymentMethod?: boolean;
  googlePayDetails?: GooglePayDetails;
  applePayDetails?: ApplePayDetails;
  cardData?: CardData;
  processor?: PaymentProcessor;
  storeId?: string;
};

export function useReloadGiftCardMutation({ onReloadSuccess }: { onReloadSuccess: () => void }) {
  const { feCountryCode } = useLocale();

  return useMutation({
    mutationFn: async (input: ReloadGiftCardMutationInput) => {
      const {
        amount,
        paymentMethodId,
        paymentMethodType,
        platform,
        storePaymentMethod,
        googlePayDetails,
        applePayDetails,
        cardData,
        processor,
        storeId,
      } = input;

      const reloadInput: ReloadGiftCardInput = {
        amount,
        paymentMethod: {
          id: paymentMethodId,
          type: mapPaymentMethodType(paymentMethodType),
        },
        currency: getCountryAndCurrencyCodes(feCountryCode).currencyCode,
        platform: platform === 'mobile' ? 'app' : (platform as 'app' | 'web' | 'kiosk'),
        storePaymentMethod,
      };

      // Handle NEW_CARD with raw card data (one-time payment, Worldpay only)
      if (paymentMethodType === FrontendPaymentMethodType.NEW_CARD && !paymentMethodId && cardData) {
        if (!processor || !storeId) {
          throw new Error('Processor and storeId required for new card encryption');
        }

        // Gift cards only supported with Worldpay in US
        if (processor !== PaymentProcessor.WORLDPAY) {
          throw new Error('Gift card reload only supports Worldpay payment processor');
        }

        // Encrypt the card data for Worldpay
        const encryptionService = new EncryptionService();
        const encryptionResult = await encryptionService.encrypt(
          cardData,
          'WORLDPAY' as any,
          storeId
        );

        if (!encryptionResult) {
          throw new Error('Failed to encrypt card data');
        }

        // Worldpay encryption result has lowValueToken
        if ('lowValueToken' in encryptionResult) {
          // Add Worldpay encrypted data
          reloadInput.worldpayInput = {
            lowValueToken: encryptionResult.lowValueToken,
          };
          // Also set the lowValueToken in paymentMethod.id for Worldpay
          reloadInput.paymentMethod.id = encryptionResult.lowValueToken;
        } else {
          throw new Error('Invalid encryption result for Worldpay');
        }

        // Add billing address
        if (cardData.postalCode) {
          reloadInput.billingAddress = {
            postalCode: cardData.postalCode,
          };
        }
      }

      // Add Google Pay token data if present
      if (googlePayDetails && paymentMethodType === FrontendPaymentMethodType.GOOGLE_PAY) {
        reloadInput.googlePay = {
          token: googlePayDetails.paymentData,
          billingAddress: googlePayDetails.billingAddress
            ? {
              postalCode: googlePayDetails.billingAddress.postalCode,
              locality: googlePayDetails.billingAddress.locality ?? undefined,
              region: googlePayDetails.billingAddress.region ?? undefined,
              streetAddress: googlePayDetails.billingAddress.streetAddress ?? undefined,
            }
            : undefined,
        };
      }

      // Add Apple Pay token data if present
      if (applePayDetails && paymentMethodType === FrontendPaymentMethodType.APPLE_PAY) {
        reloadInput.applePay = {
          token: applePayDetails.paymentData,
          signature: applePayDetails.signature,
          data: applePayDetails.data,
          version: applePayDetails.version,
          ephemeralPublicKey: applePayDetails.ephemeralPublicKey,
          publicKeyHash: applePayDetails.publicKeyHash,
          transactionId: applePayDetails.transactionId,
          billingAddress: applePayDetails.billingAddress
            ? {
              postalCode: applePayDetails.billingAddress.postalCode,
              locality: applePayDetails.billingAddress.locality ?? undefined,
              region: applePayDetails.billingAddress.region ?? undefined,
              streetAddress: applePayDetails.billingAddress.streetAddress ?? undefined,
            }
            : undefined,
        };
      }

      const { data } = await client.mutate({
        mutation: FhsReloadGiftCardInput,
        fetchPolicy: 'no-cache',
        variables: { input: reloadInput },
        refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }],
        awaitRefetchQueries: true, // Ensure Apollo cache is updated before onSuccess fires
      });

      return data?.fhsReloadGiftCard;
    },
    onSuccess: () => {
      // Invalidate React Query cache to refresh UI with new balance
      queryClient.invalidateQueries({ queryKey: ['userAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['digitalGiftCardBalance'] });
      onReloadSuccess();
    },
  });
}
